import CloseIcon from '@mui/icons-material/Close';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField } from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { AlertState } from '../../../abelcommon';
import { LoadingOverlay } from '../../../abelcommon/components/LoadingOverlay';
import { loadBalancerAPI } from '../../../api/load-balancer';
import {
  PasswordValidationError,
  passwordValidationSchema,
  PasswordValidationWarning,
} from '../../../components/PasswordValidationWarning';
import { useTranslation } from 'react-i18next';

export const UpdatePasswordModal = ({
  onCompleted,
  onClose,
}: {
  onCompleted: React.Dispatch<React.SetStateAction<AlertState>>;
  onClose: () => void;
}) => {
  const { mutate: updateUserPassword, isLoading: updateUserPasswordLoading } =
    loadBalancerAPI.hookWrapperMutation.useUpdateUserPassword();
  const isApiLoading = updateUserPasswordLoading;
  const { t } = useTranslation();

  const formik = useFormik({
    initialValues: {
      oldPassword: '',
      newPassword: '',
    },
    validationSchema: yup.object({
      oldPassword: yup.string().required(t('updatePasswordModal.currentPasswordIsRequired')),
      newPassword: passwordValidationSchema,
    }),
    onSubmit: (values, form) => {
      if (isApiLoading) return;

      updateUserPassword(
        {
          oldPassword: values.oldPassword,
          newPassword: values.newPassword,
        },
        {
          onSuccess: () => {
            onCompleted((prevState) => ({
              ...prevState,
              isOpen: true,
              severity: 'success',
              message: t('updatePasswordModal.updatePasswordSuccessfully'),
            }));
            onClose();
          },
          onError: (error) => {
            onCompleted((prevState) => ({ ...prevState, isOpen: true, severity: 'error', message: error.message }));
            form.resetForm();
          },
        }
      );
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      PaperProps={{
        component: 'form',
        onSubmit: formik.handleSubmit,
        style: { maxWidth: '500px', width: '100%' },
      }}
    >
      <DialogTitle>{t('updatePasswordModal.updatePassword')}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers sx={{ py: 4 }}>
        <TextField
          id="oldPassword"
          name="oldPassword"
          label={t('updatePasswordModal.currentPassword')}
          value={formik.values.oldPassword}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.oldPassword && Boolean(formik.errors.oldPassword)}
          helperText={formik.touched.oldPassword && formik.errors.oldPassword}
          fullWidth
          size="small"
          variant="outlined"
          sx={{ mb: 2 }}
          type="password"
        />
        <TextField
          id="newPassword"
          name="newPassword"
          label={t('updatePasswordModal.newPassword')}
          value={formik.values.newPassword}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
          helperText={
            formik.touched.newPassword && formik.errors.newPassword ? (
              <PasswordValidationWarning error={formik.errors.newPassword as unknown as PasswordValidationError} />
            ) : undefined
          }
          fullWidth
          size="small"
          variant="outlined"
          sx={{ mb: 2 }}
          type="password"
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="info" size="small">
          {t('updatePasswordModal.cancel')}
        </Button>
        <Button type="submit" variant="contained" size="small" disabled={isApiLoading}>
          {t('updatePasswordModal.submit')}
        </Button>
      </DialogActions>
      {isApiLoading && <LoadingOverlay />}
    </Dialog>
  );
};
